// Type-only, both of them: this module and the queue beside it stay free of any
// Angular or ng-bootstrap RUNTIME import, so the queue's ordering and settle rules can
// be exercised without a browser, a TestBed or a modal service.
import type { Type } from '@angular/core';
import type { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';

/**
 * INC-327 — the shape of a post-login interrupt.
 *
 * An "interrupt" is anything that wants to stop an investor on their way in: the
 * re-KYC / profile-refresh nudge, the "your vote is needed" corporate-action modal,
 * and whatever comes next. Before this they each opened raw `NgbModal` popups with no
 * coordination and avoided colliding only by incidental timing.
 */

/**
 * Rank order. LOWER SORTS FIRST — compliance ahead of anything dismissible.
 *
 * Deliberately sparse so a future interrupt can slot between two of these without
 * renumbering the ones already shipped.
 */
export const INTERRUPT_PRIORITY = {
  /** Blocks value actions (investing, withdrawal) until it is dealt with. */
  complianceGate: 10,
  /** Wants attention but the investor may walk past it. */
  dismissible: 50,
} as const;

/**
 * `gate` items keep blocking investing and withdrawal regardless of where they sit in
 * the queue — paging past one is awareness, not an escape hatch (INC-280). The kind is
 * what drives the REQUIRED chip and the persistent gate note in INC-328's frame.
 */
export type InterruptKind = 'gate' | 'dismissible';

/**
 * The host contract (INC-328).
 *
 * Both popup bodies inject `NgbActiveModal` and call `close()` / `dismiss()` on it. That
 * is correct when the body IS the modal — which is still how the single-interrupt path
 * renders it — but inside a paged frame those calls have to resolve ONE ITEM instead of
 * tearing the whole frame down. So a hosted body is handed an `InterruptHostRef` and
 * routes its outcome through that instead. No stub `NgbActiveModal`, no impersonation:
 * `host` is simply absent on the standalone path.
 */
export interface InterruptHostRef {
  /** Primary action taken ("Verify now", "Review & vote") — was `activeModal.close()`. */
  resolve(): void;
  /** Put off ("Remind me later", "Later", ×) — was `activeModal.dismiss()`. */
  defer(): void;
}

/**
 * What a popup body must accept to be hostable inside INC-328's frame.
 *
 * Both inputs are optional and absent on the single-interrupt path, where the body is
 * opened as its own modal and behaves exactly as it always has.
 */
export interface InterruptBody {
  /**
   * Where outcomes go when hosted. Absent ⇒ the body is the modal, and it talks to its
   * own `NgbActiveModal` as before.
   */
  host?: InterruptHostRef;
  /**
   * True when the frame is drawing the title bar and the close control, so a body that
   * ships its own must not draw them too. A body with no chrome of its own has nothing
   * to suppress and may ignore it.
   */
  hostOwnsChrome?: boolean;
}

export interface PostLoginInterrupt {
  /** Stable identity. Re-enqueuing the same key replaces rather than stacks. */
  key: string;
  kind: InterruptKind;
  /** See {@link INTERRUPT_PRIORITY}. Lower sorts first. */
  priority: number;
  /**
   * True only for a gate with no defer path at all — e.g. the platform-terms re-acceptance
   * gate, whose single-interrupt modalOptions already forbid Esc/backdrop dismissal because
   * accepting is the only way out. `kind: 'gate'` alone is NOT this: the pre-existing
   * profile-refresh nudge is also `kind: 'gate'` (it outranks dismissible items in the
   * queue) but explicitly supports "Remind me later" and allows Esc/backdrop on its own
   * modal. The frame reads this flag, not `kind`, to decide whether × / Esc / backdrop are
   * disabled — otherwise pairing profile-refresh with any dismissible item would silently
   * remove its existing defer path too.
   */
  mandatory?: boolean;
  /** Frame title and cover-row label (INC-328). */
  label: string;
  /** One-line cover summary (INC-328). */
  summary?: string;
  component: Type<unknown>;
  /** Set on the component instance, exactly as the old `modalRef.componentInstance.x =`. */
  inputs?: Record<string, unknown>;
  /**
   * Options for the SINGLE-interrupt path, where the body is still opened as its own
   * modal so it renders byte-for-byte as it does today. Ignored inside the frame, which
   * owns one set of options for every card.
   */
  modalOptions?: NgbModalOptions;
  /** Ran once, when this item resolves. */
  onResolve?: () => void;
  /** Ran once, when this item is deferred or the frame is closed on it. */
  onDefer?: () => void;
}

/** How an item left the queue. */
export type InterruptOutcome = 'resolve' | 'defer';

/**
 * Shared so the two entry points into the re-KYC nudge — ordinary login and
 * login-on-behalf — dedupe against each other rather than queueing two of it.
 */
export const PROFILE_REFRESH_INTERRUPT_KEY = 'profile-refresh';

export const PENDING_VOTE_INTERRUPT_KEY = 'pending-vote';

/**
 * P2P-28 · F0.3 — the platform T&C re-acceptance gate. A material T&C version means every
 * user (P2P or not) must re-accept at next login before continuing; `gate` kind at
 * `complianceGate` priority keeps investing and withdrawal locked until they do.
 */
export const PLATFORM_TERMS_REACCEPTANCE_INTERRUPT_KEY = 'platform-terms-reacceptance';

/**
 * INC-329 — where "Verify now" lands a Flagged investor.
 *
 * Moved off `LoginComponent` when the nudge moved off the login screen: the interrupt is
 * now raised after landing, from `AppComponent`, so the destination belongs with the
 * interrupt's contract rather than with the screen that used to raise it. `required` is
 * still intentionally empty — this nudge informs, it does not block.
 */
export const PROFILE_REFRESH_LANDING = ['/profile/review-attest'];
